import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import NotesForm, { NoteFormData } from '../../components/common/NotesForm';
import { fetchNote, createNote, updateNote } from '../../services/notesService';
import { useAuth } from '../../contexts/AuthContext';

interface CreateEditNoteParams {
  [key: string]: string | undefined;
  id?: string;
}

const CreateEditNotePage: React.FC = () => {
  const { id } = useParams<CreateEditNoteParams>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [initialData, setInitialData] = useState<NoteFormData | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(!!id);
  const [error, setError] = useState<string | null>(null);
  const isEdit = !!id;
  
  // Load note data if in edit mode
  useEffect(() => {
    const loadNote = async () => {
      if (!id) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        const noteData = await fetchNote(id);
        
        // Convert API note to form data
        const formData = {
          note: noteData.note,
          contributorInfo: noteData.contributorInfo,
          location: {
            zipcode: noteData.location?.zipcode || '',
            city: noteData.location?.city,
            state: noteData.location?.state
          },
          categories: noteData.categories,
          tags: noteData.tags,
          inspiration: noteData.inspiration
        };
        
        setInitialData(formData);
      } catch (err) {
        console.error('Error loading note:', err);
        setError('Failed to load the note. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    if (isEdit) {
      loadNote();
    }
  }, [id, isEdit]);
  
  // Handle form submission
  const handleSubmit = async (formData: NoteFormData) => {
    try {
      if (isEdit && id) {
        // Update existing note
        await updateNote(id, formData);
        navigate(`/my-notes/${id}`);
      } else {
        // Create new note
        const newNote = await createNote(formData);
        
        // Navigate to the new note's detail page if we have an ID, otherwise go to my-notes list
        if (newNote && newNote._id) {
          navigate(`/my-notes/${newNote._id}`);
        } else {
          // Fallback navigation to my-notes list
          navigate('/my-notes');
        }
      }
    } catch (err) {
      console.error('Error saving note:', err);
      setError('Failed to save the note. Please try again later.');
      throw err; // Re-throw to allow NotesForm to handle the error state
    }
  };

  return (
    <div className="container py-4">
      <div className="row mb-4">
        <div className="col">
          <h1 className="display-5">{isEdit ? 'Edit Note' : 'Create New Note'}</h1>
          <p className="lead">
            {isEdit 
              ? 'Update your note with new content or information.' 
              : 'Share an uplifting message with the world.'}
          </p>
        </div>
      </div>
      
      <div className="row">
        <div className="col-12">
          {error && (
            <div className="alert alert-danger mb-4" role="alert">
              {error}
            </div>
          )}
          
          {isLoading ? (
            <div className="text-center my-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading note data...</p>
            </div>
          ) : (
            <div className="card">
              <div className="card-body">
                <NotesForm
                  isEdit={isEdit}
                  initialData={initialData}
                  noteId={id}
                  onSubmit={handleSubmit}
                  isAuthenticated={!!currentUser}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateEditNotePage; 